import { useState } from 'react';
import {
  Plus,
  MoreHorizontal,
  Users,
  FolderKanban,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { useTeams, useDeleteTeam } from '@/hooks/use-teams';
import { CreateTeamDialog } from '@/features/teams/components/CreateTeamDialog';
import { TeamDetailDialog } from '@/features/teams/components/TeamDetailDialog';
import type { TeamListItem } from '@/types';

export function TeamsPage() {
  const { data: teams = [], isLoading } = useTeams();
  const deleteTeam = useDeleteTeam();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamListItem | null>(null);

  const handleDeleteTeam = async (team: TeamListItem) => {
    if (team.projectCount > 0) {
      toast({
        title: 'Cannot delete team',
        description: 'Remove team from all projects first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await deleteTeam.mutateAsync(team.id);
      toast({ title: 'Team deleted' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to delete team',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="container max-w-5xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Teams</h1>
          <p className="text-muted-foreground">
            Manage teams and their project assignments
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Team
        </Button>
      </div>

      {/* Teams List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Teams
          </CardTitle>
          <CardDescription>
            {teams.length} team{teams.length !== 1 ? 's' : ''} in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No teams yet</p>
              <p className="text-sm">Create a team to group users and assign them to projects.</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-2 pr-4">
                {teams.map((team) => (
                  <div
                    key={team.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTeam(team)}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{team.name}</p>
                        {team.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-md">
                            {team.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {team.memberCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <FolderKanban className="h-4 w-4" />
                          {team.projectCount}
                        </span>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTeam(team);
                            }}
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Manage members
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTeam(team);
                            }}
                            disabled={team.projectCount > 0}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <CreateTeamDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      
      {selectedTeam && (
        <TeamDetailDialog
          team={selectedTeam}
          open={!!selectedTeam}
          onOpenChange={(open) => !open && setSelectedTeam(null)}
        />
      )}
    </div>
  );
}
